import { Box, Text, VStack } from "@chakra-ui/react"
import type { SnowReportDay } from "../../../hooks/useResortSnowReports"

type SnowPoint = {
  date: string
  value: number
}

type SnowSeries = {
  key: string
  label: string
  points: SnowPoint[]
}

const plot = { left: 50, right: 775, top: 30, bottom: 170 }
const dayMilliseconds = 86_400_000

// Preserve separate reporting windows and locations rather than summing them.
function buildSeries(rows: SnowReportDay[]): SnowSeries[] {
  const series = new Map<string, SnowSeries>()

  for (const row of rows) {
    if (row.Outcome !== "published") continue

    for (const metric of row.Report.Metrics ?? []) {
      const key = JSON.stringify([row.Source, metric.Period, metric.Label, metric.Location])
      const entry = series.get(key) ?? {
        key,
        label: [row.Source, metric.Label, metric.Location].filter(Boolean).join(" · "),
        points: [],
      }
      entry.points.push({ date: row.Date, value: metric.Measurement })
      series.set(key, entry)
    }
  }

  return [...series.values()]
}

type ChartProps = {
  series: SnowSeries
  firstDate: string
  lastDate: string
}

function SnowReportChart({ series, firstDate, lastDate }: ChartProps) {
  const first = Date.parse(firstDate)
  const span = Math.max(dayMilliseconds, Date.parse(lastDate) - first)
  const maximum = Math.max(1, ...series.points.map((point) => point.value))
  const plotHeight = plot.bottom - plot.top
  const xForDate = (date: string) => (
    plot.left + 15 + ((Date.parse(date) - first) / span) * (plot.right - plot.left - 30)
  )
  const yForValue = (value: number) => plot.bottom - (value / maximum) * plotHeight

  return (
    <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p="4">
      <Text mb="2" fontWeight="medium">{series.label} (cm)</Text>
      <svg
        viewBox="0 0 800 210"
        role="img"
        aria-label={`${series.label}, centimeters by resort local date`}
        style={{ width: "100%", maxHeight: 260 }}
      >
        {[0, 0.5, 1].map((fraction) => {
          const value = maximum * fraction
          const y = yForValue(value)

          return (
            <g key={fraction}>
              <line
                x1={plot.left} y1={y} x2={plot.right} y2={y}
                stroke="#4a5568" strokeDasharray="3 5"
              />
              <text x={plot.left - 10} y={y + 4} textAnchor="end" fill="#cbd5e0" fontSize="12">
                {value.toFixed(1)}
              </text>
            </g>
          )
        })}
        {series.points.map((point) => {
          const x = xForDate(point.date)
          const y = yForValue(point.value)

          return (
            <g key={point.date}>
              <title>{point.date}: {point.value} cm</title>
              <line x1={x} y1={plot.bottom} x2={x} y2={y} stroke="#63b3ed" strokeWidth="4" />
              <circle cx={x} cy={y} r="4" fill="#90cdf4" />
            </g>
          )
        })}
        <text x={plot.left} y="198" fill="#cbd5e0" fontSize="12">{firstDate}</text>
        <text x={plot.right} y="198" textAnchor="end" fill="#cbd5e0" fontSize="12">{lastDate}</text>
      </svg>
    </Box>
  )
}

export default function SnowReportCharts({ rows }: { rows: SnowReportDay[] }) {
  const series = buildSeries(rows)

  if (!series.length) {
    return <Text color="gray.400">No confirmed daily reports to chart yet.</Text>
  }

  return (
    <VStack align="stretch" gap="5">
      {series.map((entry) => (
        <SnowReportChart
          key={entry.key}
          series={entry}
          firstDate={rows[0].Date}
          lastDate={rows[rows.length - 1].Date}
        />
      ))}
    </VStack>
  )
}
